import React, { useState, useMemo } from "react";
import moment from "moment";
import { formatAmountWithCurrency } from "../../../../../../utils/CurrencyUtils";
import DateCell from "../DateCell";
import {
  StepHeader,
  Panel,
  Table,
  Empty,
  Notice,
  byDate,
  paymentName,
  StatusPill,
  CountPill,
  formatStatus,
  rowStyle,
} from "../primitives";

const MOBILISATION_NAME = /mobilis|mobiliz/i;

const tdStyle = { padding: "10px 16px" };
const tdDarkStyle = { padding: "10px 16px", color: "#0d0d0e" };

/**
 * Step 4 — customer payment dates.
 *
 * Nothing derives these. There is no link in the data between a payment line
 * and a billing milestone, and an RNPL plan has no mobilisation line at all
 * (its lines are downpayment → instalments → move-in), so there is nothing to
 * calculate from. Every date here moves because someone moved it.
 *
 * The bulk shift exists for the one case that comes up: mobilisation slips,
 * work has not commenced, and the customer should not be paying instalments
 * for it yet. It is a shortcut for editing each unpaid row by hand — same
 * drafts, same preview, same save.
 */
const CustomerPaymentsStep = ({
  schedule,
  editable,
  preview,
  paymentDrafts,
  setPaymentDrafts,
}) => {
  // Server dates, not drafts — a bulk shift must not reshuffle the table
  // under the user while they read it.
  const lines = useMemo(
    () => [...(schedule.customer_payments || [])].sort(byDate((l) => l.due_date)),
    [schedule.customer_payments]
  );
  const plan = schedule.payment_plan || {};
  // Kept from the portal source (unused there too).
  // eslint-disable-next-line no-unused-vars
  const paid = lines.filter((l) => l.locked).length;
  const shiftable = lines.filter((l) => editable && l.editable_date);

  // A payment that follows a billing milestone moves with the activity dates
  // edited two steps earlier, which are not saved yet — so the stored due_date
  // is stale here. The preview already worked out where it lands.
  const projected = new Map(
    (preview?.plan?.payment_lines || []).map((line) => [String(line.id), line.to])
  );

  // Hand-typed always beats derived, so the draft is checked first.
  const shownDate = (line) => {
    if (paymentDrafts[line.id] !== undefined) return paymentDrafts[line.id];
    const derived = projected.get(String(line.id));
    return derived !== undefined ? derived : (line.due_date ?? null);
  };

  // How far Mobilisation has moved in this edit — the natural default for the
  // shift. Falls back to 0 when the scope has no Mobilisation milestone.
  const suggestedShift = useMemo(() => {
    const mobilisation = (schedule.billing_milestones || []).find((g) =>
      MOBILISATION_NAME.test(g.name || "")
    );
    if (!mobilisation) return 0;

    const planned = (preview?.plan?.groups || []).find(
      (g) => g.group_id === mobilisation.group_id
    );
    if (!planned?.from || !planned?.to) return 0;

    return moment(planned.to).diff(moment(planned.from), "days");
  }, [schedule.billing_milestones, preview]);

  const [shiftDays, setShiftDays] = useState("");
  const days = Number(shiftDays === "" ? suggestedShift : shiftDays);

  const applyShift = () => {
    if (!Number.isFinite(days) || days === 0) return;

    setPaymentDrafts((prev) => {
      const next = { ...prev };
      for (const line of shiftable) {
        // Shift from where the date actually lands, derived value included —
        // shifting off the stale stored date would undo the derivation.
        const current = shownDate(line);
        if (!current) continue;
        next[line.id] = moment(current).add(days, "days").format("YYYY-MM-DD");
      }
      return next;
    });
  };

  const resetShift = () => {
    setPaymentDrafts((prev) => {
      const next = { ...prev };
      for (const line of shiftable) delete next[line.id];
      return next;
    });
    setShiftDays("");
  };

  const dirtyCount = shiftable.filter(
    (l) => paymentDrafts[l.id] !== undefined
  ).length;

  const applyDisabled = !Number.isFinite(days) || days === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <StepHeader
        title="Customer payments"
        right={
          <CountPill>
            {lines.length} payments
            {plan.type ? ` · ${plan.type}` : ""}
          </CountPill>
        }
      />

      {plan.type === "RNPL" && (
        <Notice tone="warning">
          <span style={{ fontWeight: 500 }}>This is an RNPL instalment plan.</span>{" "}
          Nothing here moves on its own — a schedule change never shifts an
          instalment. Move a date only when the work it pays for has not
          commenced, so the customer is not charged ahead of the job.
        </Notice>
      )}

      {editable && shiftable.length > 0 && (
        <Panel style={{ padding: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "14px", color: "#374151" }}>
              Shift the {shiftable.length} unpaid payment
              {shiftable.length === 1 ? "" : "s"} by
            </span>
            <input
              type="number"
              value={shiftDays === "" ? suggestedShift : shiftDays}
              onChange={(e) => setShiftDays(e.target.value)}
              style={{
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
                padding: "4px 8px",
                fontSize: "14px",
                width: "80px",
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            <span style={{ fontSize: "14px", color: "#374151" }}>days</span>
            <button
              onClick={applyShift}
              disabled={applyDisabled}
              style={{
                padding: "4px 12px",
                borderRadius: "8px",
                border: "none",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: "#4D6BF6",
                opacity: applyDisabled ? 0.4 : 1,
                cursor: applyDisabled ? "not-allowed" : "pointer",
              }}
            >
              Apply
            </button>
            {dirtyCount > 0 && (
              <button
                onClick={resetShift}
                style={{
                  padding: "4px 12px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#fff",
                  color: "#374151",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            )}
          </div>
          <p
            style={{
              fontSize: "12px",
              color: "#6B7280",
              marginTop: "8px",
              marginBottom: 0,
            }}
          >
            {suggestedShift !== 0
              ? `Mobilisation moved ${suggestedShift} day${
                  Math.abs(suggestedShift) === 1 ? "" : "s"
                } in this edit — suggested as the default.`
              : ""}
          </p>
        </Panel>
      )}

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {!lines.length ? (
          <Empty>No customer payments on this scope.</Empty>
        ) : (
          <Table columns={["Payment", "Amount", "Status", "Due date"]}>
            {lines.map((line) => (
              <tr key={line.id} style={rowStyle}>
                <td style={tdDarkStyle}>
                  {paymentName(line, formatStatus)}
                  {/* Which milestone it tracks — otherwise a date that moved on
                      its own looks like it moved for no reason. */}
                  {line.billing_milestone_name && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginTop: "2px",
                      }}
                    >
                      follows {line.billing_milestone_name}
                    </div>
                  )}
                </td>
                <td style={tdDarkStyle}>
                  {line.amount ? formatAmountWithCurrency(line.amount, "black-xs-small") : "-"}
                </td>
                <td style={tdStyle}>
                  <StatusPill status={line.status} />
                </td>
                <td style={tdStyle}>
                  <DateCell
                    value={shownDate(line)}
                    originalValue={line.due_date}
                    onChange={(date) =>
                      setPaymentDrafts((prev) => ({ ...prev, [line.id]: date }))
                    }
                    locked={!editable || !line.editable_date}
                    lockReason={line.locked ? "Already paid" : undefined}
                    hideLockIcon
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
};

export default CustomerPaymentsStep;
